import React, { AnchorHTMLAttributes, useEffect, useRef, useState } from 'react';
import cls from 'classnames';
import styles from './modal.module.scss';
import { createPortal } from 'react-dom';
import FontAwesomeIcon from '../fa_icon';

export type BasicModalProps = {
  open: boolean;
  onClose: () => void;
}

type Props = {
  className?: string;
  contentClassName?: string;
} & BasicModalProps;

const modalRoot = document.createElement('div');
modalRoot.classList.add(styles.root);
document.body.appendChild(modalRoot);

/**
 * Standard modal.
 */
export const Modal: React.FC<Props> = ({ children, open, onClose, className, contentClassName }) => {
  const [completelyHidden, setCompletelyHidden] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setCompletelyHidden(false);
    }

    const keyboardHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    }

    window.addEventListener('keyup', keyboardHandler);
    return () => window.removeEventListener('keyup', keyboardHandler);
  }, [open]);

  return createPortal(
    <div
      ref={e => modalRef.current = e}
      className={cls([styles.modal, open ? styles.open : null, className])}
      onTransitionEnd={(e) => {
        if (e.currentTarget === modalRef.current && !open) {
          setCompletelyHidden(true);
        }
      }}
      onClick={() => onClose?.()}>
      <div
        className={cls([styles.content, contentClassName])}
        onClick={e => e.stopPropagation()}>
        {
          typeof onClose === 'function' ? (
            <ModalClose onClick={() => onClose()} />
          ) : null
        }
        {completelyHidden ? null : children}
      </div>
    </div>,
    modalRoot
  );
}

export const ModalTitle: React.FC = ({ children }) => (
  <h2 className={styles.title}>
    {children}
  </h2>
)

export const ModalFooter: React.FC = ({ children }) => (
  <footer className={styles.footer}>
    {children}
  </footer>
)

export const ModalClose: React.FC<AnchorHTMLAttributes<any>> = (props) => (
  <a {...props} className={cls([styles.close, props.className])}>
    <FontAwesomeIcon name="times" />
  </a>
)